/**
 * <carousel-dots>
 *
 * Renders clickable page navigation for the nearest <slideshow-component>.
 * Builds one dot per "page" (group of visible slides), not per slide.
 * Syncs via slideshow:select event. Rebuilds on resize (mobile ↔ desktop).
 */
class CarouselDots extends HTMLElement {
  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {HTMLElement | null} */
  #slideshow = null;

  /** @type {HTMLElement[]} */
  #dots = [];

  /** @type {HTMLElement | null} */
  #fill = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  connectedCallback() {
    if (!this.#bind()) {
      this.#watchForScroller();
    }
  }

  disconnectedCallback() {
    this.#teardown();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#mutationObserver?.disconnect();
    this.#mutationObserver = null;
  }

  #teardown() {
    this.#slideshow?.removeEventListener('slideshow:select', this.#onSelect);
    this.#scroller = null;
    this.#slideshow = null;
    this.#dots = [];
  }

  /**
   * Walks up ancestors to find a container with a <slideshow-slides> descendant.
   * Prefers the scroller not inside `.hidden--mobile`.
   * @returns {boolean} true when a scroller and slideshow were found and bound.
   */
  #bind() {
    let el = this.parentElement;
    /** @type {HTMLElement | null} */
    let scroller = null;

    while (el) {
      const scrollers = /** @type {HTMLElement[]} */ ([...el.querySelectorAll('slideshow-slides')]);
      if (scrollers.length) {
        scroller = scrollers.find((s) => !s.closest('.hidden--mobile')) || scrollers[0] || null;
        break;
      }
      el = el.parentElement;
    }

    if (!scroller) return false;

    const slideshow = /** @type {HTMLElement | null} */ (scroller.closest('slideshow-component'));
    if (!slideshow) return false;

    this.#scroller = scroller;
    this.#slideshow = slideshow;

    slideshow.addEventListener('slideshow:select', this.#onSelect);
    
    this.#resizeObserver = new ResizeObserver(() => this.#render());
    this.#resizeObserver.observe(scroller);
    
    this.#render();
    return true;
  }

  #render() {
    const slides = /** @type {HTMLElement[]} */ ([...this.#scroller.querySelectorAll(':scope > slideshow-slide')]);
    const totalSlides = slides.length;

    if (totalSlides < 2) {
      this.hidden = true;
      return;
    }

    // perPage = how many slides visible at once (measure from DOM, not slideshow.visibleSlides)
    const perPage = this.#measureVisibleCount(slides);
    const dotsCount = Math.ceil(totalSlides / perPage);

    this.innerHTML = '';
    this.#dots = [];

    // Create wrapper track (intrinsic width, not 100%)
    const track = document.createElement('div');
    track.className = 'carousel-dots__track';
    this.appendChild(track);

    for (let i = 0; i < dotsCount; i++) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'carousel-dots__dot';
      btn.setAttribute('aria-label', `Page ${i + 1}`);
      btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      btn.dataset.index = String(i);
      btn.addEventListener('click', this.#handleDotClick);
      track.appendChild(btn);
      this.#dots.push(btn);
    }

    // Create animated fill overlay AFTER dots (so offsetLeft is correct)
    const fill = document.createElement('div');
    fill.className = 'carousel-dots__fill';
    track.appendChild(fill);
    this.#fill = fill;

    this.hidden = dotsCount < 2;
    requestAnimationFrame(() => this.#setActiveDot(0));
  }

  #measureVisibleCount(slides) {
    if (!slides.length) return 1;
    
    const slideWidth = slides[0].getBoundingClientRect().width;
    const scrollerWidth = this.#scroller.getBoundingClientRect().width;
    
    if (slideWidth <= 0) return 1;
    
    // Use floor: if 1.5 slides fit, perPage=1 (each swipe shows 1 new slide)
    const visible = scrollerWidth / slideWidth;
    return Math.max(1, Math.floor(visible));
  }

  #handleDotClick = (event) => {
    const btn = /** @type {HTMLElement} */ (event.currentTarget);
    const pageIndex = parseInt(btn.dataset.index ?? '0', 10);
    
    const slides = this.#scroller.querySelectorAll(':scope > slideshow-slide');
    const perPage = this.#measureVisibleCount([...slides]);
    const slideIndex = pageIndex * perPage;
    
    /** @type {any} */ (this.#slideshow)?.select(slideIndex);
  };

  #onSelect = (/** @type {CustomEvent} */ event) => {
    if (event.target !== this.#slideshow) return;
    
    const currentSlide = /** @type {any} */ (this.#slideshow)?.current ?? 0;
    const slides = this.#scroller.querySelectorAll(':scope > slideshow-slide');
    const perPage = this.#measureVisibleCount([...slides]);
    const currentPage = Math.floor(currentSlide / perPage);
    
    this.#setActiveDot(currentPage);
  };

  #setActiveDot(pageIndex) {
    // Update fill position (slides to active dot)
    if (this.#fill && this.#dots.length > 0) {
      const firstDot = this.#dots[0];
      const activeDot = this.#dots[pageIndex] || firstDot;
      const offset = activeDot.offsetLeft - firstDot.offsetLeft;
      this.#fill.style.transform = `translateX(${offset}px)`;
    }
    
    // Keep aria-selected for accessibility
    this.#dots.forEach((dot, i) => {
      dot.setAttribute('aria-selected', i === pageIndex ? 'true' : 'false');
    });
  }

  #watchForScroller() {
    let root = /** @type {HTMLElement | null} */ (this.parentElement);
    while (root && !root.id) root = root.parentElement;
    root = root || document.body;

    this.#mutationObserver = new MutationObserver(() => {
      if (this.#bind()) {
        this.#mutationObserver?.disconnect();
        this.#mutationObserver = null;
      }
    });
    this.#mutationObserver.observe(root, { childList: true, subtree: true });
  }
}

if (!customElements.get('carousel-dots')) {
  customElements.define('carousel-dots', CarouselDots);
}
